import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '../service/auth.service';
import { buildDailyDashboard, buildDashboard, DailyDashboard, DashboardRevenueData, DashboardTransaction, ExpiringBooking, indiaDate, RevenueBreakdown, RevenuePeriod, slotsOf, statusKey } from './dashboard-data';

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private dailyCache = new Map<string, DailyDashboard>();

  constructor(private auth: AuthService) {}

  load(tenantId: number, roleId: number) {
    const key = 'bookingdexter.dashboard.v2.' + JSON.stringify([this.auth.getUsername(), tenantId, roleId]);
    return this.auth.getDashboardWorklists(tenantId).pipe(
      map((response: any) => {
        if (!Array.isArray(response?.today) || !Array.isArray(response?.pending) || !Array.isArray(response?.expiries)) {
          throw new Error('Invalid dashboard worklist response');
        }
        const transactions: DashboardTransaction[] = response.today;
        const now = new Date();
        let daily = this.readDaily(key, indiaDate(now).format('YYYY-MM-DD'));
        if (!daily) {
          daily = buildDailyDashboard(transactions, now);
          this.dailyCache.set(key, daily);
          try {
            localStorage.setItem(key, JSON.stringify(daily));
          } catch {}
        }
        const dashboard = buildDashboard(transactions, now, daily);
        dashboard.pending = response.pending;
        dashboard.pendingAmount = Number(response.pendingAmount || 0);
        dashboard.expiries = this.expiringBookings(response.expiries, now);
        return dashboard;
      }),
      catchError(error => this.loadError('Transactions', error))
    );
  }

  private expiringBookings(transactions: DashboardTransaction[], now: Date): ExpiringBooking[] {
    const today = indiaDate(now).startOf('day');
    const lastExpiryDay = today.clone().add(3, 'months').endOf('day');
    return transactions.reduce((expiries, transaction) => expiries.concat(slotsOf(transaction)
      .filter(slot => statusKey(slot.status) === 'booked' && !!slot.endDate && indiaDate(slot.endDate).isValid())
      .map(slot => ({
        transaction,
        service: slot.productName,
        venue: slot.venueName,
        endDate: indiaDate(slot.endDate).format('YYYY-MM-DD'),
        daysRemaining: indiaDate(slot.endDate).startOf('day').diff(today, 'days')
      }))
      .filter(item => item.daysRemaining >= 0 && !indiaDate(item.endDate).isAfter(lastExpiryDay))), [] as ExpiringBooking[]);
  }

  loadMonthlyRevenue(tenantId: number): Observable<DashboardRevenueData> {
    return this.auth.getDashboardRevenue(tenantId, 'monthly').pipe(
      map((response: any) => {
        if (!Array.isArray(response?.buckets) || !Array.isArray(response?.breakdown)) throw new Error('Invalid monthly revenue response');
        return {
          buckets: response.buckets.map((bucket: any) => ({ ...bucket, totalRevenue: Number(bucket.totalRevenue || 0), cancelled: Number(bucket.cancelled || 0) })) as RevenuePeriod[],
          breakdown: response.breakdown.map((row: any) => ({ ...row, productId: Number(row.productId), venueId: Number(row.venueId), totalRevenue: Number(row.totalRevenue || 0), cancelled: Number(row.cancelled || 0) })) as RevenueBreakdown[]
        };
      }),
      catchError(error => this.loadError('Monthly revenue', error))
    );
  }

  private readDaily(key: string, date: string): DailyDashboard | undefined {
    const memory = this.dailyCache.get(key);
    if (memory?.date === date) return memory;
    this.dailyCache.delete(key);
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (saved?.date === date && Array.isArray(saved.months) && saved.months.length === 6
        && saved.months.every(month => typeof month.key === 'string' && typeof month.label === 'string' && Number.isFinite(month.totalRevenue))
        && Array.isArray(saved.expiries) && saved.expiries.every(row => row?.transaction && Number.isFinite(row.daysRemaining))) {
        this.dailyCache.set(key, saved);
        return saved;
      }
      localStorage.removeItem(key);
    } catch {}
    return undefined;
  }

  private loadError(source: string, error: any): Observable<never> {
    let detail = 'Please try again.';
    if (error?.status === 0) {
      detail = 'Cannot reach the server. Check your connection and try again.';
    } else if (error?.status === 401) {
      detail = 'Your session has expired. Please sign in again.';
    } else if (error?.status === 403) {
      detail = 'You do not have permission to access this data.';
    } else if (typeof error?.status === 'number') {
      detail = `The server returned HTTP ${error.status}. Please try again.`;
    } else if (error instanceof Error) {
      detail = `${error.message}. Please try again.`;
    }
    return throwError(new Error(`Dashboard ${source.toLowerCase()} could not be loaded. ${detail}`));
  }

}
